// components/designer/PropertyPanel.tsx
/**
 * Property Panel Component
 *
 * Right sidebar with tabs for:
 * - Properties: Position, size, z-index, rotation, styling
 * - Configuration: Field-level configuration using ConfigurationTab
 */

import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { ScrollArea } from "@shared/ui/scroll-area";
import { Separator } from "@shared/ui/separator";
import { FileText, Maximize2, Settings } from "lucide-react";
import React, { useEffect } from "react";
import { generateGraphHtml } from "../../generateGraphHtml";
import {
  ComponentStyle,
  FieldDefinition,
  NormalizedComponent,
} from "../../types";
import ConfigurationTab from "../designer/ConfigurationTab";

interface PropertyPanelProps {
  selectedComponent: NormalizedComponent | null;
  propertyTab: "properties" | "configuration";
  onPropertyTabChange: (tab: "properties" | "configuration") => void;
  onUpdateComponent: (
    id: string,
    updates: Partial<NormalizedComponent>
  ) => void;
  onUpdateStyle: (id: string, style: Partial<ComponentStyle>) => void;
  components: NormalizedComponent[];
  getFields: (id: string) => FieldDefinition[];
  onFieldUpdate: (componentId: string, fieldName: string, updates: any) => void;
  onToggleField: (componentId: string, fieldName: string) => void;
  onToggleLabel: (componentId: string, fieldName: string) => void;
  onReorderField: (
    componentId: string,
    fieldName: string,
    direction: "up" | "down"
  ) => void;
  onContentUpdate: (componentId: string, content: string) => void;
  onUpdateImageUrl: (componentId: string, imageUrl: string) => void;
  isInitialized: boolean;
  configUpdateCounter: number;
}

const PropertyPanel: React.FC<PropertyPanelProps> = ({
  selectedComponent,
  propertyTab,
  onPropertyTabChange,
  onUpdateComponent,
  onUpdateStyle,
  components,
  getFields,
  onFieldUpdate,
  onToggleField,
  onToggleLabel,
  onReorderField,
  onContentUpdate,
  onUpdateImageUrl,
  isInitialized,
  configUpdateCounter,
}) => {
  const handleUpdateGraphConfig = (
    componentId: string,
    updates: Partial<any>
  ) => {
    if (!selectedComponent || selectedComponent.id !== componentId) return;

    const currentConfig = selectedComponent.graphConfig || {
      utilityService: "Gas",
      numberOfMonths: 6,
      showXAxis: true,
      showYAxis: true,
      showValues: true,
    };

    const newConfig = {
      ...currentConfig,
      ...updates,
    };

    // Regenerate HTML with new config
    const newHtml = generateGraphHtml(newConfig);

    onUpdateComponent(componentId, {
      graphConfig: newConfig,
      templateHtml: newHtml,
      originalHtml: newHtml, // Update original HTML too so it persists
    });
  };

  // Generate/update HTML for graph components when selected
  const hasInitialized = React.useRef<Set<string>>(new Set());

  useEffect(() => {
    if (
      selectedComponent &&
      selectedComponent.alias === "usage_history_graph" &&
      !hasInitialized.current.has(selectedComponent.id)
    ) {
      const currentConfig = selectedComponent.graphConfig || {
        utilityService: "Gas",
        numberOfMonths: 6,
        showXAxis: true,
        showYAxis: true,
        showValues: true,
      };

      // Always regenerate HTML to ensure it matches the current config
      const newHtml = generateGraphHtml(currentConfig);

      hasInitialized.current.add(selectedComponent.id);

      onUpdateComponent(selectedComponent.id, {
        graphConfig: currentConfig,
        templateHtml: newHtml,
        originalHtml: newHtml,
      });
    }
  }, [selectedComponent?.id, selectedComponent?.alias]);

  return (
    <div className="h-full bg-white border-l flex flex-col">
      {/* Tab Header */}
      <div className="border-b">
        <div className="flex">
          <button
            onClick={() => onPropertyTabChange("properties")}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              propertyTab === "properties"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Settings size={16} />
              Properties
            </div>
          </button>
          <button
            onClick={() => onPropertyTabChange("configuration")}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              propertyTab === "configuration"
                ? "border-blue-500 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileText size={16} />
              Configuration
            </div>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          {propertyTab === "properties" ? (
            // PROPERTIES TAB
            selectedComponent ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Component
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedComponent.type}
                  </p>
                </div>

                <Separator />

                {/* Position & Size */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Position & Size
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="x" className="text-xs">
                        X
                      </Label>
                      <Input
                        id="x"
                        type="number"
                        value={selectedComponent.x}
                        onChange={(e) =>
                          onUpdateComponent(selectedComponent.id, {
                            x: Number(e.target.value),
                          })
                        }
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label htmlFor="y" className="text-xs">
                        Y
                      </Label>
                      <Input
                        id="y"
                        type="number"
                        value={selectedComponent.y}
                        onChange={(e) =>
                          onUpdateComponent(selectedComponent.id, {
                            y: Number(e.target.value),
                          })
                        }
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label htmlFor="width" className="text-xs">
                        Width
                      </Label>
                      <Input
                        id="width"
                        type="number"
                        value={selectedComponent.width}
                        onChange={(e) =>
                          onUpdateComponent(selectedComponent.id, {
                            width: Number(e.target.value),
                          })
                        }
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label htmlFor="height" className="text-xs">
                        Height
                      </Label>
                      <Input
                        id="height"
                        type="number"
                        value={selectedComponent.height}
                        onChange={(e) =>
                          onUpdateComponent(selectedComponent.id, {
                            height: Number(e.target.value),
                          })
                        }
                        className="h-8"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label htmlFor="zIndex" className="text-xs">
                      Z-Index
                    </Label>
                    <Input
                      id="zIndex"
                      type="number"
                      value={selectedComponent.zIndex}
                      onChange={(e) =>
                        onUpdateComponent(selectedComponent.id, {
                          zIndex: Number(e.target.value),
                        })
                      }
                      className="h-8"
                      min="1"
                    />
                  </div>
                </div>

                <Separator />

                {/* Style */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Style
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor="bgColor" className="text-xs">
                        Background
                      </Label>
                      <div className="flex items-center space-x-2">
                        <input
                          id="bgColor"
                          type="color"
                          value={
                            selectedComponent.style?.backgroundColor ||
                            "#ffffff"
                          }
                          onChange={(e) =>
                            onUpdateStyle(selectedComponent.id, {
                              backgroundColor: e.target.value,
                            })
                          }
                          className="w-8 h-8 border rounded cursor-pointer"
                        />
                        <Input
                          type="text"
                          value={
                            selectedComponent.style?.backgroundColor ||
                            "#ffffff"
                          }
                          onChange={(e) =>
                            onUpdateStyle(selectedComponent.id, {
                              backgroundColor: e.target.value,
                            })
                          }
                          className="flex-1 h-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="fontSize" className="text-xs">
                        Font Size
                      </Label>
                      <Input
                        id="fontSize"
                        type="number"
                        value={selectedComponent.style?.fontSize || 14}
                        onChange={(e) =>
                          onUpdateStyle(selectedComponent.id, {
                            fontSize: Number(e.target.value),
                          })
                        }
                        className="h-8"
                        min="8"
                        max="72"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 mt-8">
                <Maximize2 size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a component</p>
                <p className="text-xs mt-2">Click on canvas or layer list</p>
              </div>
            )
          ) : // CONFIGURATION TAB
          isInitialized && selectedComponent ? (
            <ConfigurationTab
              components={components}
              selectedComponentId={selectedComponent.id}
              onFieldUpdate={onFieldUpdate}
              onToggleField={onToggleField}
              onToggleLabel={onToggleLabel}
              onReorderField={onReorderField}
              getFields={getFields}
              onContentUpdate={onContentUpdate}
              onUpdateStyle={onUpdateStyle}
              onUpdateImageUrl={onUpdateImageUrl}
              onUpdateGraphConfig={handleUpdateGraphConfig}
              key={`${selectedComponent.id}-${configUpdateCounter}`}
            />
          ) : (
            <div className="text-center text-gray-500 mt-8">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Select a component</p>
              <p className="text-xs mt-2">
                {!isInitialized
                  ? "Template system initializing..."
                  : "Click on canvas or layer list"}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default PropertyPanel;
